import { baseUrl } from "../api/services";
import { isMockMode } from "../api/mock/mode";
import { clearSession, getRefreshToken, getToken, updateTokens } from "./session";

/**
 * Access tokenni yangilash.
 *
 * Access token qisqa (15 daqiqa). Ilgari panel refresh tokenni umuman
 * ishlatmasdi: 15 daqiqadan keyin birinchi so'rov 401 olib, xodim
 * login sahifasiga otilardi va ochiq forma yo'qolardi.
 *
 * Refresh token AYLANADI (rotatsiya): eskisi bekor qilinadi, bekor
 * qilingani qayta ishlatilsa server foydalanuvchining BARCHA
 * tokenlarini yopadi. Shuning uchun yangilash bitta: bir oynadagi
 * parallel so'rovlar bitta va'dani kutadi, bir nechta oyna esa
 * `navigator.locks` orqali navbatma-navbat yangilaydi va qulf ichida
 * boshqa oyna allaqachon yangilaganini tekshiradi.
 */

/** Muddati shuncha qolganda token oldindan yangilanadi. */
const SKEW_MS = 60_000;

let inflight: Promise<string | null> | null = null;

/** JWT `exp` (ms). O'qib bo'lmasa `null` — token yaroqli deb olinadi. */
function expiresAt(token: string): number | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const payload = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isFresh(token: string): boolean {
  const exp = expiresAt(token);
  return exp === null || exp - Date.now() > SKEW_MS;
}

/** So'rov uchun token: muddati tugayotgan bo'lsa avval yangilanadi. */
export async function freshToken(): Promise<string | null> {
  const token = getToken();
  if (!token || isMockMode() || isFresh(token)) return token;
  return refreshSession(token);
}

/**
 * Sessiyani yangilaydi. `rejected` — server rad etgan (yoki eskirgan)
 * token: qulf ichida u allaqachon almashtirilgan bo'lsa yangisi
 * qaytariladi, ikkinchi yangilash qilinmaydi.
 */
export function refreshSession(rejected: string): Promise<string | null> {
  inflight ??= exclusive(() => run(rejected)).finally(() => {
    inflight = null;
  });
  return inflight;
}

async function exclusive<T>(task: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request("uyda.auth.refresh", task);
  }
  return task();
}

async function run(rejected: string): Promise<string | null> {
  const current = getToken();
  if (current && current !== rejected && isFresh(current)) return current;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearSession();
    return null;
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl("identity")}/api/v1/auth/refresh`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // Tarmoq yo'q — sessiya saqlanadi: ulanish tiklanganda qayta
    // urinib ko'riladi, xodim chiqarib yuborilmaydi.
    return null;
  }

  if (!response.ok) {
    // Refresh rad etildi (muddati o'tgan, bekor qilingan) — qayta kirish.
    clearSession();
    return null;
  }
  const body = (await response.json().catch(() => undefined)) as
    | { data?: { access_token?: string; refresh_token?: string } }
    | undefined;
  const access = body?.data?.access_token;
  const next = body?.data?.refresh_token;
  if (!access || !next) {
    clearSession();
    return null;
  }
  updateTokens(access, next);
  return access;
}
